import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { AuthService } from '../auth.service';
import { assetUrl } from '@app/shared/utils/asset-url.util';
import { NotificationService } from '@app/shared/notification/notification.service';
import { ClientAccessService } from '@app/events/shared/client-access.service';

@Component({
  selector: 'app-reset-password',
  imports: [FormsModule, NgOptimizedImage, NgClass, RouterLink],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent implements OnInit {
  email = '';
  password = '';
  passwordConfirmation = '';
  token = '';

  // TVG-43: Client Access invitation mode. When the route carries an
  // `?invitation=<token>` query param the screen validates the token against
  // the public Client Access endpoints, then sets the password and logs the
  // contact straight into the customer portal instead of routing to /login.
  protected invitationMode = false;
  protected invitationToken = '';
  protected invitationName = '';
  protected invitationEventName = '';
  protected invitationInvalid = false;
  protected validatingInvitation = false;

  protected error?: string = undefined;
  protected success?: string = undefined;
  protected submitting = false;
  protected showPassword = false;
  protected showPasswordConfirmation = false;
  protected readonly logoUrl = assetUrl('images/the-venue-group-logo.svg');
  protected readonly passwordRequirements = {
    minLength: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasNumber: false,
    hasSpecialChar: false,
  };
  protected passwordMeetsAllRequirements = false;
  protected get passwordsDoNotMatch(): boolean {
    return !!this.password && !!this.passwordConfirmation && this.password !== this.passwordConfirmation;
  }

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private clientAccessService: ClientAccessService,
  ) {}

  ngOnInit(): void {
    // Get token and email from URL query params
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || '';
      this.email = params['email'] || '';

      const invitation = params['invitation'] || '';
      if (invitation) {
        this.invitationMode = true;
        this.invitationToken = invitation;
        this.validateInvitation();
      }
    });
  }

  /**
   * TVG-43: validate the invitation token. On success show the contact's
   * email/name + event so they know what they are setting a password for. A
   * 410 means the link is invalid/expired/used.
   */
  private validateInvitation(): void {
    this.validatingInvitation = true;
    this.invitationInvalid = false;
    this.error = undefined;

    this.clientAccessService.validateInvitation(this.invitationToken).subscribe({
      next: (response) => {
        this.validatingInvitation = false;
        this.email = response.data.email;
        this.invitationName = response.data.name;
        this.invitationEventName = response.data.event_name;
      },
      error: (response) => {
        this.validatingInvitation = false;
        this.invitationInvalid = true;
        this.error = response.error?.error?.message
          || 'This invitation link is invalid or has expired. Please ask the venue team to resend your invitation.';
      },
    });
  }

  submit() {
    this.error = undefined;
    this.success = undefined;
    this.onPasswordInput();

    if (this.password !== this.passwordConfirmation) {
      this.error = 'Passwords do not match.';
      return;
    }

    if (!this.passwordMeetsAllRequirements) {
      this.error = 'Password does not meet the required strength rules.';
      return;
    }

    if (this.invitationMode) {
      this.submitInvitation();
      return;
    }

    this.submitting = true;

    this.authService.resetPassword({
      token: this.token,
      email: this.email,
      password: this.password,
      password_confirmation: this.passwordConfirmation
    }).subscribe({
      next: (response) => {
        this.submitting = false;
        const successMessage = response.message || 'Password has been reset successfully.';
        this.success = successMessage;
        this.notificationService.success(successMessage, 'Password Updated');
        setTimeout(() => {
          this.router.navigate(['/login']);
        }, 2000);
      },
      error: (response) => {
        this.submitting = false;
        this.error = response.error?.message || response.error?.error || 'Unable to reset password. Please try again.';
      }
    });
  }

  /**
   * TVG-43: set the password against the invitation token. The endpoint
   * returns a Sanctum bearer token — store it exactly like the login flow
   * (localStorage 'token') and route the contact into the customer portal.
   */
  private submitInvitation(): void {
    if (this.invitationInvalid) {
      return;
    }

    this.submitting = true;

    this.clientAccessService.setPassword(
      this.invitationToken,
      this.password,
      this.passwordConfirmation,
    ).subscribe({
      next: (response) => {
        localStorage.setItem('token', response.data.token);
        const successMessage = 'Your password has been set. Taking you to your portal…';
        this.success = successMessage;
        this.notificationService.success(successMessage, 'Welcome');
        this.authService.fetchUser().subscribe({
          next: () => {
            this.submitting = false;
            this.router.navigate(['/my-events']);
          },
          error: () => {
            this.submitting = false;
            this.router.navigate(['/my-events']);
          },
        });
      },
      error: (response) => {
        this.submitting = false;
        if (response.status === 410) {
          this.invitationInvalid = true;
          this.error = response.error?.error?.message
            || 'This invitation link is invalid or has expired. Please ask the venue team to resend your invitation.';
          return;
        }
        this.error = response.error?.message
          || response.error?.error
          || 'Unable to set your password. Please try again.';
      },
    });
  }

  protected onPasswordInput(): void {
    const password = this.password || '';

    this.passwordRequirements.minLength = password.length >= 8;
    this.passwordRequirements.hasUpperCase = /\p{Lu}/u.test(password);
    this.passwordRequirements.hasLowerCase = /\p{Ll}/u.test(password);
    this.passwordRequirements.hasNumber = /\p{N}/u.test(password);
    this.passwordRequirements.hasSpecialChar = /[^\p{L}\p{N}\s]/u.test(password);

    this.passwordMeetsAllRequirements =
      this.passwordRequirements.minLength &&
      this.passwordRequirements.hasUpperCase &&
      this.passwordRequirements.hasLowerCase &&
      this.passwordRequirements.hasNumber &&
      this.passwordRequirements.hasSpecialChar;
  }
}
